<template>
  <section
    v-element-visibility="scrolledIntoView"
   
  >
    <THeading
      :level="3"
      tag="h3"
    >
      {{ t(`${translationPrefix}.title`) }}
    </THeading>
    <TSubHeading>
      {{
        t(`${translationPrefix}.content`, {
          start: formatAsShortDateYear(bookingEstimateParams.dateStart),
          end: formatAsShortDateYear(bookingEstimateParams.dateEnd),
        })
      }}
    </TSubHeading>
    <div class="relative w-full min-h-75 mt-6">
      <AtomTransition timing="fast">
        <LazySearchFormFieldDates
          v-if="showDateRange"
          :dates="{ start: bookingEstimateParams.dateStart, end: bookingEstimateParams.dateEnd }"
          :timezone="rvComputedTimezone"
          :disabled-dates="disabledDates"
          :disabled="!rvIsPublished"
          :unavailable-days="rvUnavailableDays"
          :min-date="rvMinBookableDate"
          :max-date="rvMaxBookableDate"
          show-clear-button
          data-test-id-suffix="availability"
          :one-column="!isMdBreakpoint"
          @update:dates="(value) => updateDates(value)"
        />
        <div
          v-else
          class="flex justify-center items-center absolute inset-0"
        />
      </AtomTransition>
    </div>

    <TTipbox
      v-if="isReadyToBook"
      variant="highlight"
      no-icon
      class="mt-4"
    >
      <div class="flex items-center w-full">
        <span>{{ t('popularDates') }}</span>
        <EstimateButton
         
          size="sm"
          inline-button
          class="ml-auto"
        />
      </div>
    </TTipbox>

    <EstimateErrorTipbox class="mt-4" />
  </section>
</template>

<script setup lang="ts">
import { vElementVisibility } from '@vueuse/components'
import type { SearchDates } from '~/types/search'

const { t } = useI18n()
const { formatAsShortDateYear } = useDates()

const showDateRange = ref(false)

const {
  rvBookingSeasonStart,
  rvBookingSeasonEnd,
  rvCalendars,
  rvIsPublished,
  rvMinBookableDate,
  rvMaxBookableDate,
  rvRVSellingDate,
  rvUnavailableDays,
  rvComputedTimezone,
  rvIsStatic,
} = useRvDetails()

const disabledDates = computed(() => {
  return [
    ...getRvCalendarBlockedRangesFromSellingDate(rvRVSellingDate.value, rvComputedTimezone.value),
    ...getRvCalendarBlockedRangesFromBookingSeason(rvBookingSeasonStart.value, rvBookingSeasonEnd.value, rvComputedTimezone.value),
    ...getRvCalendarBlockedDates(rvCalendars.value, rvComputedTimezone.value),
  ]
})

const { bookingEstimateParams, isBookable, hasIncompleteDestination } = useBookingEstimate()

const translationPrefix = computed(() => {
  if (isBookable.value) {
    return 'available'
  }

  return 'check'
})

function scrolledIntoView(isVisible: boolean) {
  if (isVisible) {
    showDateRange.value = true
  }
}

const { isMdBreakpoint } = useBreakpoint()

const { updateDateRange } = useBookingEstimate()
const route = useRoute()
async function updateDates(newDates: SearchDates) {
  updateDateRange({
    dateStart: newDates?.start || null,
    dateEnd: newDates?.end || null,
  })

  await navigateTo({
    query: {
      ...route.query,
      StartDate: newDates?.start,
      EndDate: newDates?.end,
    },
  })
}

const { getExperimentVariant } = useExperiment()
const isReadyToBook = computed(() => {
  if (getExperimentVariant('rvz28661SimplifiedRtbFlow') !== 'variant') {
    return isBookable.value
  }

  // Static listings have no destination step
  return isBookable.value && (rvIsStatic.value || !hasIncompleteDestination.value)
})
</script>

<i18n lang="json">
{
  "en": {
    "check": {
      "title": "Check availability",
      "content": "Select your trip dates"
    },
    "available": {
      "title": "Available for your dates",
      "content": "{start} - {end}"
    },
    "popularDates": "These dates are popular! Request to book while they’re still free."
  },
  "fr": {
    "check": {
      "title": "Vérifier la disponibilité",
      "content": "Sélectionnez vos dates de voyage"
    },
    "available": {
      "title": "Disponible pour vos dates",
      "content": "{start} - {end}"
    },
    "popularDates": "Ces dates sont populaires! Demandez de les réserver tant qu’elles sont encore disponbles."
  }
}
</i18n>
